import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {
    getHumanReadableDueUnit,
    getHumanReadableSingularDueUnit,
} from 'nutshell-core/date-time/delays';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';

import type {
    GetEditionsThatWillSendImmediatelyWithDelayChange as GetEditionsSentWithDelayChangeQuery,
    GetEditionsThatWillSendImmediatelyWithDelayChangeVariables as GetEditionsSentWithDelayChangeQueryVariables,
    TimespanInput,
} from 'nutshell-graphql-types';

import GET_EDITIONS_THAT_WILL_SEND_IMMEDIATELY_WITH_DELAY_CHANGE from '../graphql/queries/get-editions-that-will-send-immediately-with-delay-change.graphql';

type Props = {
    editionId: string;
    editionName: string;
    onCancel: () => void;
    onConfirmSaveChanges: () => void;
    delay: TimespanInput | null | undefined;
};

export function EditActiveDelayConfirmation(props: Props) {
    const [shouldShowConfirmation, setShouldShowConfirmation] = React.useState<boolean>(false);

    const {data, loading} = useQuery<
        GetEditionsSentWithDelayChangeQuery,
        GetEditionsSentWithDelayChangeQueryVariables
    >(GET_EDITIONS_THAT_WILL_SEND_IMMEDIATELY_WITH_DELAY_CHANGE, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
        variables: {
            editionId: props.editionId,
            delay: props.delay,
        },
    });

    const {onConfirmSaveChanges} = props;

    React.useEffect(() => {
        if (!loading) {
            if (data && data.dripEditionsThatWillSendImmediatelyWithDelayChange) {
                setShouldShowConfirmation(true);
            } else {
                onConfirmSaveChanges();
            }
        }
    }, [loading, data, onConfirmSaveChanges]);

    function getConfirmationMessage(
        numWaited: number | null | undefined,
        newDelay: TimespanInput | null | undefined,
        messageName: string
    ) {
        const numWaitedString = numWaited ? numWaited.toString() : 'Any'; // Use 'Any' as fallback (shouldn't ever happen)

        let recipientsText = `${numWaitedString} ${
            numWaited !== 1 ? 'queued recipients ' : 'queued recipient '
        }`;

        if (newDelay) {
            const timespan = newDelay.timespan;
            const readableUnit =
                timespan === 1
                    ? getHumanReadableSingularDueUnit(newDelay.unit)
                    : getHumanReadableDueUnit(newDelay.unit);

            recipientsText = recipientsText
                .concat(numWaited !== 1 ? 'who have waited ' : 'who has waited ')
                .concat(`${timespan} or more ${readableUnit} `);
        }

        return (
            <span>
                {recipientsText} will immediately receive <b>{messageName}</b>. Are you sure you’d
                like to proceed?
            </span>
        );
    }

    return (
        <>
            {shouldShowConfirmation ? (
                <ConfirmationDialog
                    message={getConfirmationMessage(
                        data ? data.dripEditionsThatWillSendImmediatelyWithDelayChange : undefined,
                        props.delay,
                        props.editionName
                    )}
                    confirmText={`Yes, ${props.delay ? 'change' : 'delete'} delay`}
                    cancelText={`No, don’t ${props.delay ? 'change' : 'delete'}`}
                    onCancel={() => {
                        setShouldShowConfirmation(false);
                        props.onCancel();
                    }}
                    onConfirm={() => {
                        setShouldShowConfirmation(false);
                        onConfirmSaveChanges();
                    }}
                    isConfirmDestructive={true}
                />
            ) : undefined}
        </>
    );
}
